import { F0Box, F0Select, F0Text } from '@factorialco/f0-react'
import { Input, Stack, Switch } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

interface Props {
  priorityMode: string
  slaMode: string
  slaFixedHours: number | null
  slaCriticalHours: number | null
  slaHighHours: number | null
  slaMediumHours: number | null
  slaLowHours: number | null
  pauseSlaOnWaiting: boolean
  // eslint-disable-next-line factorial/no-boolean-null
  onChange: (field: string, value: string | number | boolean | null) => void
}

const SLA_PRIORITY_FIELDS = [
  {
    field: 'slaCriticalHours',
    label: () =>
      `${i18n.t('ticketing.settings.categories.form.priority_levels.critical')} — ${i18n.t('ticketing.settings.categories.form.sla_hours')}`,
  },
  {
    field: 'slaHighHours',
    label: () =>
      `${i18n.t('ticketing.settings.categories.form.priority_levels.high')} — ${i18n.t('ticketing.settings.categories.form.sla_hours')}`,
  },
  {
    field: 'slaMediumHours',
    label: () =>
      `${i18n.t('ticketing.settings.categories.form.priority_levels.medium')} — ${i18n.t('ticketing.settings.categories.form.sla_hours')}`,
  },
  {
    field: 'slaLowHours',
    label: () =>
      `${i18n.t('ticketing.settings.categories.form.priority_levels.low')} — ${i18n.t('ticketing.settings.categories.form.sla_hours')}`,
  },
] as const

const SlaConfig = ({
  priorityMode,
  slaMode,
  slaFixedHours,
  slaCriticalHours,
  slaHighHours,
  slaMediumHours,
  slaLowHours,
  pauseSlaOnWaiting,
  onChange,
}: Props) => {
  const isFixedPriority = priorityMode === 'fixed'

  const slaModeOptions = [
    {
      value: 'sla_per_priority',
      label: i18n.t('ticketing.settings.categories.form.sla_modes.sla_per_priority'),
      disabled: isFixedPriority,
    },
    {
      value: 'fixed_sla',
      label: i18n.t('ticketing.settings.categories.form.sla_modes.fixed_sla'),
    },
    {
      value: 'no_sla',
      label: i18n.t('ticketing.settings.categories.form.sla_modes.no_sla'),
    },
  ]

  const handleHoursChange = (field: string, value: string) => {
    const num = value === '' ? null : parseInt(value, 10)
    onChange(field, num)
  }

  const hoursValues: Record<string, number | null> = {
    slaCriticalHours,
    slaHighHours,
    slaMediumHours,
    slaLowHours,
  }

  return (
    <Stack gap="3">
      <F0Select
        label={i18n.t('ticketing.settings.categories.form.sla_mode')}
        value={slaMode}
        onChange={(value: string) => onChange('slaMode', value)}
        options={slaModeOptions}
        required
      />

      {slaMode === 'fixed_sla' && (
        <Input
          label={i18n.t('ticketing.settings.categories.form.sla_hours')}
          type="number"
          value={slaFixedHours != null ? String(slaFixedHours) : ''}
          onChange={(value) => handleHoursChange('slaFixedHours', value)}
          required
        />
      )}

      {slaMode === 'sla_per_priority' && (
        <Stack gap="2">
          {SLA_PRIORITY_FIELDS.map(({ field, label }) => {
            const value = hoursValues[field]
            return (
              <Input
                key={field}
                label={label()}
                type="number"
                value={value != null ? String(value) : ''}
                onChange={(v) => handleHoursChange(field, v)}
                required
              />
            )
          })}
        </Stack>
      )}

      {slaMode !== 'no_sla' && (
        <Stack gap="1">
          <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
            <Switch
              id="pause-sla-toggle"
              checked={pauseSlaOnWaiting}
              onCheckedChange={(checked) => onChange('pauseSlaOnWaiting', checked)}
            />
            <F0Text content={i18n.t('ticketing.settings.categories.form.pause_sla')} />
          </F0Box>
          <F0Text
            variant="description"
            content={i18n.t('ticketing.settings.categories.form.pause_sla_description')}
          />
        </Stack>
      )}
    </Stack>
  )
}

export default SlaConfig
